// SubscriptionForm.jsx
import React, { useState } from 'react';
import { Container, Spinner } from 'react-bootstrap';
import { useStripe, useElements, PaymentElement } from '@stripe/react-stripe-js';
import axios from 'axios';
import { getAccessToken, getUser } from '../services/AuthService';
import { Navigate } from 'react-router-dom';
import { showErrorNotification } from '../services/notificationService';

export default function SubscriptionForm({
  isLoggedIn,
  selectedPriceId,
  trialDays,
  selectDefaultPaymentMethod, // optional callback from parent
  mode,           // 'setup' | 'payment' (decided in SubscriptionPage)
  clientSecret,   // provided by <Elements />, not used directly here
}) {
  const stripe = useStripe();
  const elements = useElements();

  const [loading, setLoading] = useState(false);
  const [subSuccess, setSubSuccess] = useState(false);

  if (!isLoggedIn) {
    return <Navigate to="/" replace />;
  }

  const BASE = process.env.REACT_APP_BASE_URL;

  const authHeaders = () => ({
    Authorization: `Bearer ${getAccessToken()}`,
  });

  const normalizeTrialDays = () => {
    if (Array.isArray(trialDays) && trialDays.length > 0) {
      const v = Number(trialDays[0]?.days);
      return Number.isFinite(v) ? v : 0;
    }
    if (typeof trialDays === 'number') return trialDays;
    return 0;
  };

  const refreshUser = async () => {
    const u = getUser();
    const { data } = await axios.get(
      `${BASE}/api/users/${u.id}/`,
      { headers: authHeaders(), timeout: 5000 }
    );
    localStorage.setItem('satnam.user', JSON.stringify(data));
    return data;
  };

  // Sync endpoint to avoid the race with the webhook (no-trial path)
  const syncSubscription = async () => {
    await axios.post(
      `${BASE}/api/stripe/sync-subscription/`,
      {},
      { headers: authHeaders(), timeout: 12000 }
    );
  };

  // Creates the subscription on your backend after saving a PM (trial path)
  const createSubscriptionWithPM = async (paymentMethodId) => {
    const u = getUser();
    const trial = normalizeTrialDays();

    // NOTE: matches your current backend route (PaymentDetailView.post)
    return axios.post(
      `${BASE}/api/create_subscription/${u.id}/`,
      { price_id: selectedPriceId, payment_method_id: paymentMethodId, trial },
      { headers: authHeaders(), timeout: 12000 }
    );
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!stripe || !elements) return;
    if (!selectedPriceId) {
      showErrorNotification(new Error('Selecciona un plan antes de continuar.'));
      return;
    }

    setLoading(true);
    try {
      if (mode === 'setup') {
        // Save card first (no charge yet)
        const result = await stripe.confirmSetup({
          elements,
          redirect: 'if_required',
        });
        if (result.error) throw result.error;

        const setupIntent = result.setupIntent;
        const paymentMethodId = setupIntent?.payment_method;
        if (!paymentMethodId) {
          throw new Error('No se obtuvo payment_method del SetupIntent.');
        }

        // Create the subscription using that PM (trial path)
        await createSubscriptionWithPM(paymentMethodId);

        // Let parent optionally store the default PM in UI state
        if (typeof selectDefaultPaymentMethod === 'function') {
          selectDefaultPaymentMethod(paymentMethodId);
        }

        // Refresh user (should already be trialing)
        await refreshUser();

        setSubSuccess(true);
      } else {
        // mode === 'payment' (first invoice payment)
        const result = await stripe.confirmPayment({
          elements,
          redirect: 'if_required',
        });
        if (result.error) throw result.error;

        // Confirm subscription state in backend and then refresh user
        await syncSubscription();
        await refreshUser();

        setSubSuccess(true);
      }
    } catch (err) {
      showErrorNotification(err);
    } finally {
      setLoading(false);
    }
  };

  if (subSuccess) {
    return <Navigate to="/sub-success" replace />;
  }

  return (
    <Container className="my-3">
      <h5 className="mb-3">Suscripción</h5>

      <form onSubmit={onSubmit}>
        <PaymentElement />
        <button
          className="btn btn-primary mt-3"
          type="submit"
          disabled={loading || !stripe || !elements}
        >
          {loading
            ? (
              <>
                <Spinner animation="border" role="status" size="sm" className="me-2" />
                Procesando…
              </>
            )
            : (mode === 'setup' ? 'Guardar método y suscribirme' : 'Suscribirme')}
        </button>
      </form>
    </Container>
  );
}
